import { ContactReportRole } from "encamp-prisma/generated/browser";

import { prettyPrintEnumValue } from "./prettyPrintEnumValue";

// CA submissions read the owner/operator from the CA-specific pair and the
// generic role, so all three are locked.
export const LOCKED_OWNER_OPERATOR_ROLES: readonly ContactReportRole[] = [
  ContactReportRole.EPCRA_CA_OWNER,
  ContactReportRole.EPCRA_CA_OPERATOR,
  ContactReportRole.EPCRA_OWNER_OPERATOR,
];

const LOCKED_OWNER_OPERATOR_ROLE_VALUES = new Set<string>(LOCKED_OWNER_OPERATOR_ROLES);

// Accepts the Prisma enum (server) or GraphQL enum (client); same string values.
export function isLockedOwnerOperatorRole(role: ContactReportRole | string): boolean {
  return LOCKED_OWNER_OPERATOR_ROLE_VALUES.has(role);
}

export function rolesIncludeLockedOwnerOperator(
  roles: readonly (ContactReportRole | string)[]
): boolean {
  return roles.some(isLockedOwnerOperatorRole);
}

/**
 * Maps ContactReportRole enum values to human-readable labels.
 * Works with both Prisma enum format (SCREAMING_SNAKE_CASE) and
 * GraphQL enum format (PascalCase).
 *
 * Falls back to prettyPrintEnumValue for any unknown roles,
 * making it future-proof for new roles added to the enum.
 */
export function contactReportRoleToLabel(role: ContactReportRole | string): string {
  // Normalize to Prisma format (SCREAMING_SNAKE_CASE) for lookup
  const normalizedRole = normalizeToScreamingSnakeCase(role);

  const roleLabels: Record<string, string> = {
    // California-specific EPCRA roles
    [ContactReportRole.EPCRA_CA_OPERATOR]: "Operator",
    [ContactReportRole.EPCRA_CA_OWNER]: "Owner",
    [ContactReportRole.EPCRA_CA_PROPERTY_OWNER]: "Property Owner",
    [ContactReportRole.EPCRA_CA_TANK_OPERATOR]: "Tank Operator",
    [ContactReportRole.EPCRA_CA_TANK_OWNER]: "Tank Owner",

    // Standard EPCRA roles
    [ContactReportRole.EPCRA_OWNER_OPERATOR]: "Owner / Operator",
    [ContactReportRole.EPCRA_EMERGENCY_CONTACT]: "Emergency Contact",
    [ContactReportRole.EPCRA_EMERGENCY_COORDINATOR]: "Emergency Coordinator",
    [ContactReportRole.EPCRA_TIER_II_CONTACT]: "Tier II Contact",
    [ContactReportRole.EPCRA_BILLING]: "Billing Contact",
    [ContactReportRole.EPCRA_CHEMICAL_CARRIER]: "Chemical Carrier",
    [ContactReportRole.EPCRA_T2M_DIRECT_SITE_COMMUNICATION]: "Direct Site Communication",
    [ContactReportRole.EPCRA_T2M_PARENT_COMPANY]: "Parent Company",
    [ContactReportRole.EPCRA_EMERGENCY_EQUIPMENT_CONTACT]: "Emergency Equipment Contact",
    [ContactReportRole.EPCRA_REGIONAL_ENVIRONMENTAL_CONTACT]: "Regional Environmental Contact",

    // General role
    [ContactReportRole.GENERAL]: "General",

    // RCRA roles
    [ContactReportRole.RCRA_AUTHORIZED_REPRESENTATIVE]: "RCRA Authorized Representative",
    [ContactReportRole.RCRA_BILLING_CONTACT]: "RCRA Billing Contact",
    [ContactReportRole.RCRA_HURRICANE_POINT_OF_CONTACT]: "RCRA Hurricane Point of Contact",
    [ContactReportRole.RCRA_OWNER]: "RCRA Owner",
    [ContactReportRole.RCRA_OPERATOR]: "RCRA Operator",
    [ContactReportRole.RCRA_REAL_PROPERTY_OWNER]: "RCRA Real Property Owner",
    [ContactReportRole.RCRA_SITE_CONTACT]: "RCRA Site Contact",
    [ContactReportRole.RCRA_SITE_MANAGER]: "RCRA Site Manager",
  };

  // Return mapped label or fall back to prettyPrintEnumValue for unknown roles
  return roleLabels[normalizedRole] ?? prettyPrintEnumValue(role);
}

/**
 * Converts PascalCase or camelCase to SCREAMING_SNAKE_CASE.
 * Already SCREAMING_SNAKE_CASE strings are returned as-is.
 */
function normalizeToScreamingSnakeCase(value: string): string {
  // If it's already SCREAMING_SNAKE_CASE (contains underscore and is uppercase), return as-is
  if (value.includes("_") && value === value.toUpperCase()) {
    return value;
  }

  // Convert PascalCase/camelCase to SCREAMING_SNAKE_CASE
  return value
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .toUpperCase();
}
